import React, { Component } from 'react';
import GetRequest, { PostRequest } from "../../utils/FetchHelper";
import URL from "../../utils/url";
import { Card, Col, Layout, Row, DatePicker, Space, Spin, Input, Typography, Tabs, Select } from 'antd';
import "./serverHealth.scss";
import _ from 'lodash';
import HealthChart from './healthChart';
import dayjs from "dayjs";
import { Message } from '../../variables/general';
import { getAggregationTime } from '../../utils/Utilities';
import CustomButton from '../../common/CustomButton/Button';

const { RangePicker } = DatePicker;
const { Title } = Typography;
const { TabPane } = Tabs;

const AGGREGATION_STRATEGY_SUM = 'Sum';
const AGGREGATION_STRATEGY_AVERAGE = 'Average';
class ServerHealth extends Component {
    controller = new window.AbortController();
    state = {
        serverHealthData: [],
        isLoading: false,
        endTime: new Date().toISOString(),
        startTime: (new Date(new Date() - (15 * 60 * 1000))).toISOString(),
        chartReady: [],
        entriesCount: 0,
        enablePrint: false,
        activeTab: '1',
        customMetricName: '',
        customMetricGroup: '',
        hasSearched: false,
        statisticTypes: [],
        selectedStatisticType: '',
        aggregationStrategy: '',
        aggregationOptions: [
            { label: AGGREGATION_STRATEGY_SUM, value: AGGREGATION_STRATEGY_SUM },
            { label: AGGREGATION_STRATEGY_AVERAGE, value: AGGREGATION_STRATEGY_AVERAGE }
        ],
    }
    componentDidMount() {
        this.getHealthData();
    }

    getStatisticTypes = () => {
        GetRequest(URL.listStatsTpe, this.controller.signal).then(resp => {
            const statisticTypes = Array.isArray(resp) ? resp : [];
            const { selectedStatisticType, aggregationStrategy } = this.state;
            this.setState({
                statisticTypes: statisticTypes,
                // Auto-select STATS_TYPE_LIST (value: 1) if not already selected
                selectedStatisticType: selectedStatisticType || 1,
                // Auto-select Average if not already selected  
                aggregationStrategy: aggregationStrategy || AGGREGATION_STRATEGY_AVERAGE
            });
        }).catch((err) => {
            console.error("Error loading statistic types:", _.get(err, "message", ""));
            this.setState({
                statisticTypes: []
            });
        });
    }

    componentWillUnmount() {
        if (this.controller) this.controller.abort();
    }

    onTabChange = (activeTab) => {
        const { isLoading, statisticTypes } = this.state;
        if (isLoading) {
            return;
        }
        
        // Reset time range to last 15 minutes from now
        const endTime = new Date().toISOString();
        const startTime = (new Date(new Date() - (15 * 60 * 1000))).toISOString();
        
        this.setState({
            activeTab: activeTab,
            chartReady: [],
            entriesCount: 0,
            enablePrint: false,
            serverHealthData: [],
            customMetricName: '',
            customMetricGroup: '',
            selectedStatisticType: activeTab === '2' ? 1 : '', // Auto-select STATS_TYPE_LIST (value: 1)
            aggregationStrategy: activeTab === '2' ? AGGREGATION_STRATEGY_AVERAGE : '', // Auto-select Average
            hasSearched: false,
            startTime: startTime,
            endTime: endTime
        }, () => {
            if (activeTab === '1') {
                this.getHealthData();
            } else if (activeTab === '2' && statisticTypes.length === 0) {
                this.getStatisticTypes();
            }
        });
    }

    onCustomMetricNameChange = (e) => {
        const value = e.target.value;
        const { customMetricName } = this.state;
        if (value !== customMetricName) {
            this.setState({
                customMetricName: value
            });
        }
    }

    onCustomMetricGroupChange = (e) => {
        const value = e.target.value;
        const { customMetricGroup } = this.state;
        if (value !== customMetricGroup) {
            this.setState({
                customMetricGroup: value
            });
        }
    }

    onStatisticTypeChange = (value) => {
        const { selectedStatisticType } = this.state;
        if (value !== selectedStatisticType) {
            this.setState({
                selectedStatisticType: value
            });
        }
    }

    onAggregationStrategyChange = (value) => {
        const { aggregationStrategy } = this.state;
        if (value !== aggregationStrategy) {
            this.setState({
                aggregationStrategy: value
            });
        }
    }

    onCustomMetricSearch = () => {
        const { customMetricName } = this.state;
        if (customMetricName.trim()) {
            this.setState({
                chartReady: [],
                entriesCount: 0,
                enablePrint: false,
                hasSearched: true,
            }, () => {
                this.getHealthData();
            });
        } else {
            Message.error("Validation Error", "Please enter a metric name to search.");
        }
    }

    onDateChange = (dates) => {
        try {
            const startTime = dates[0].toISOString();
            const endTime = dates[1].toISOString();
            this.setState({
                startTime: startTime,
                endTime: endTime,
            }, () => {
                const { activeTab, hasSearched, customMetricName } = this.state;
                if (activeTab === '1' || (activeTab === '2' && hasSearched && customMetricName.trim())) {
                    this.getHealthData();
                }
            });
        } catch (err) {
            console.log(err);
        }
    }

    getHealthData = () => {
        const { startTime, endTime, activeTab, customMetricName, customMetricGroup, selectedStatisticType, aggregationStrategy } = this.state;
        const aggreagationTime = getAggregationTime(startTime, endTime);
        this.setState((prevState) => ({
            ...prevState,
            isLoading: true,
        }))
        const payload = {
            startTime: startTime,
            endTime: endTime,
            aggregationTime: aggreagationTime,
        };
        if (activeTab === '2' && customMetricName.trim()) {
            payload.metricDetails = {
                name: customMetricName.trim(),
                group: customMetricGroup.trim() || "",
                statisticType: selectedStatisticType,
                strategy: aggregationStrategy
            };
        }
        PostRequest(URL.getServerHealth, this.controller.signal, payload, (resp) => {
            const Entries = _.get(resp, "Entries", []).filter(p => {
                const datapoints = _.get(p, "Datapoints");
                return datapoints !== null && !_.isEmpty(datapoints);
            });
            
            // Apply original filtering only for Tab 1
            const filteredEntries = activeTab === '1' 
                ? Entries.filter(p => 
                    !(_.get(p, "Name") === "API Router" && (_.get(p, "Datapoints", []) || []).length === 0)
                  )
                : Entries;
            
            const entriesCount = (filteredEntries || []).length;
            const serverHealthData = filteredEntries.map(profile => {
                const datapoints = (_.get(profile, "Datapoints", []) || []).map(datapoint => ({
                    ...datapoint,
                    Timestamp: new Date(_.get(datapoint, "Timestamp", "")),
                    MetricValue: _.get(datapoint, "MetricValue", "") > 1 ? 1 : _.get(datapoint, "MetricValue", ""),
                }));
                
                return {
                    ...profile,
                    Datapoints: datapoints
                };
            });
            this.setState({
                serverHealthData: serverHealthData,
                isLoading: false,
                entriesCount: entriesCount,
                chartReady: []
            });
        }, (err) => {
            this.setState({
                isLoading: false,
            });
            Message.error("Error Loading Server Health data", _.get(err, "message", ""));
        });
    }

    refreshHandle = () => {
        const { activeTab, hasSearched } = this.state;
        const endTime = new Date().toISOString();
        const startTime = (new Date(new Date() - (15 * 60 * 1000))).toISOString();
        this.setState((prevState) => ({
            ...prevState,
            startTime: startTime,
            endTime: endTime,
            entriesCount: 0,
            enablePrint: false,
            chartReady: [],
        }), () => {
            if (activeTab === '1' || (activeTab === '2' && hasSearched)) {
                this.getHealthData();
            }
        });
    }

    chartReady = () => {
        const { chartReady, entriesCount } =this.state;
        if (chartReady.length >= entriesCount) {
            return;
        }
        const readyCount = [...chartReady, true];
        this.setState({
            chartReady: readyCount,
            enablePrint: readyCount.length >= entriesCount
        });
    }

    print = () => {
        const { activeTab } = this.state;
        const title = document.title;
        const reportTitle = activeTab === '2'
            ? 'Custom Metrics Report'
            : 'Server Health Report';
        document.title = `${reportTitle} ${new Date().toISOString()}`;
        window.print();
        document.title = title;
    }

    clearMetricName = () => {
        this.setState({
            customMetricName: ''
        });
    }
    // Use a memoized tabContent to prevent re-renders
    getTabContent() {
        const { serverHealthData, isLoading, startTime, endTime, activeTab, customMetricName, customMetricGroup, hasSearched, statisticTypes, selectedStatisticType, aggregationStrategy, aggregationOptions } = this.state;
        return (
            <>
                <Row justify="space-between" wrap style={{ marginBottom: 16 }}>
                    <Col id='no-print-server-health'>
                        {activeTab === '2' && (
                            <div>
                                <Space wrap size="middle" style={{ marginBottom: 12 }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        <span>Metric Group:</span>
                                        <Input
                                            placeholder="Enter metric group"
                                            value={customMetricGroup}
                                            onChange={this.onCustomMetricGroupChange}
                                            onPressEnter={this.onCustomMetricSearch}
                                            style={{ width: 300 }}
                                            allowClear
                                        />
                                    </div>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        <span>Metric Name:</span>
                                        <Input
                                            placeholder="Enter metric name"
                                            value={customMetricName}
                                            onChange={this.onCustomMetricNameChange}
                                            onPressEnter={this.onCustomMetricSearch}
                                            style={{ width: 200 }}
                                            allowClear
                                            onClear={this.clearMetricName}
                                        />
                                    </div>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        <span>Statistic Type:</span>
                                        <Select
                                            placeholder="Select statistic type"
                                            value={selectedStatisticType}
                                            onChange={this.onStatisticTypeChange}
                                            style={{ width: 200 }}
                                            allowClear
                                            options={statisticTypes.map(type => ({
                                                label: type.label,
                                                value: type.value
                                            }))}
                                        />
                                    </div>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        <span>Aggregation Strategy:</span>
                                        <Select
                                            placeholder="Select aggregation strategy"
                                            value={aggregationStrategy}
                                            onChange={this.onAggregationStrategyChange}
                                            style={{ width: 200 }}
                                            allowClear
                                            options={aggregationOptions}
                                        />
                                    </div>
                                    <CustomButton
                                        type="primary"
                                        onClick={this.onCustomMetricSearch}
                                    >
                                        Search
                                    </CustomButton>
                                </Space>
                            </div>
                        )}
                    </Col>
                    <Col className="time-range-col" style={{ marginTop: 16 }}>
                        <Space>
                            Time Range
                            <RangePicker
                                showTime
                                value={[dayjs(startTime), dayjs(endTime)]}
                                defaultValue={[dayjs().add(-15, "minutes").add(-1, "seconds"), dayjs()]}
                                ranges={{
                                    "Last 15 min": [dayjs().add(-15, "minutes").add(-1, "seconds"), dayjs()],
                                    "Last 1 Hour": [dayjs().add(-1, "hours").add(-1, "seconds"), dayjs()],
                                    "Last 6 Hours": [dayjs().add(-6, "hours").add(-1, "seconds"), dayjs()],
                                    "Last 1 Day": [dayjs().add(-1, "days").add(-1, "seconds"), dayjs()],
                                    "Last 1 Week": [dayjs().add(-7, "days").add(-1, "seconds"), dayjs()],
                                }}
                                onChange={this.onDateChange} />
                        </Space>
                    </Col>
                </Row>
                <Layout className="server-health-layout">
                    {isLoading && <Spin />}
                    {!isLoading && activeTab === '2' && hasSearched && serverHealthData.length === 0 && (
                        <div style={{ textAlign: 'center', padding: '40px' }}>
                            <Typography.Text type="secondary">
                                No custom metrics data available for the selected metric.
                            </Typography.Text>
                        </div>
                    )}
                    {!isLoading && activeTab === '1' && serverHealthData.length === 0 && (
                        <div style={{ textAlign: 'center', padding: '40px' }}>
                            <Typography.Text type="secondary">
                                No server health data available for the selected time range.
                            </Typography.Text>
                        </div>
                    )}
                    <Row gutter={[16, 16]}>
                        {!isLoading && serverHealthData.map((profile, key) => {
                            return (
                                <Col span={12}>
                                    <Card key={key} className={"health-card"} title={_.get(profile, "Name", "")} size="small">
                                        <HealthChart
                                            data={_.get(profile, "Datapoints", []) || []}
                                            id={_.get(profile, "Name", "") + key}
                                            key={key}
                                            aggregationTime={getAggregationTime(startTime, endTime)}
                                            onReady={this.chartReady}
                                        />
                                    </Card>
                                </Col>
                            );
                        })}
                    </Row>
                </Layout>
            </>
        );

    }
    render() {
        const { activeTab, isLoading, enablePrint } = this.state;
        const tabContent = this.getTabContent();
        return (
            <div id='server-health-layout'>
                <Row justify="space-between" style={{ marginBottom: 16 }}>
                    <Col>
                        <Title level={3}>
                            Metrics Dashboard
                        </Title>
                    </Col>
                    <Col id='no-print-server-health'>
                        <Space className="mt-24">
                            {enablePrint && (
                            <CustomButton
                                shape="round"
                                type="default"
                                onClick={this.print}
                            >Print
                            </CustomButton>
                            )}
                            <CustomButton
                                shape="round"
                                type="default"
                                onClick={this.refreshHandle}
                            >Refresh
                            </CustomButton>
                        </Space>
                    </Col>
                </Row>
                
                <Tabs 
                    activeKey={activeTab} 
                    onChange={this.onTabChange} 
                    destroyInactiveTabPane={true}
                    className={isLoading ? 'tabs-disabled' : ''}
                >
                    <TabPane tab="Server Health" key="1" disabled={isLoading}>
                        {tabContent}
                    </TabPane>
                    <TabPane tab="Custom Metrics" key="2" disabled={isLoading}>
                        {tabContent}
                    </TabPane>
                </Tabs>
            </div>
        );
    }
}


export default ServerHealth;